import { useInfiniteQuery } from '@tanstack/react-query';
import { TableOverviewParams, TableOverviewResponse } from '@gettactic/api';
import { api } from '@/lib/api/api';
import { differenceInDays, isSameWeek, parseISO, startOfWeek } from 'date-fns';

export function useTableOverview(
  filters: TableOverviewParams,
  options: {
    enabled?: boolean;
    getNextPageParam?: (
      lastPage: TableOverviewResponse | null
    ) => string | null;
    keepPreviousData?: boolean;
  }
) {
  // Set default limit if not provided

  const defaultOptions = {
    enabled: true,
    getNextPageParam: (lastPage: TableOverviewResponse | null) => {
      if (!lastPage?.cursor) {
        return null;
      }
      return lastPage.cursor;
    },
    keepPreviousData: true
  };

  if (!filters.limit) {
    filters.limit = 30;
  }

  let processedFilters = { ...filters };
  if (processedFilters.date && processedFilters.date_sort) {
    try {
      const filterDate = parseISO(processedFilters.date);
      const filterSortDate = parseISO(processedFilters.date_sort);
      const monday = startOfWeek(filterDate, { weekStartsOn: 1 });
      const isNotSameWeek = !isSameWeek(monday, filterSortDate);

        // If not same week, remove the sort_date, as it will fire an error
        if (isNotSameWeek) {
          const { date_sort, date_sort_order, ...filtersWithoutSort } = processedFilters;
          processedFilters = filtersWithoutSort;
        }
    } catch (error) {
      // If date parsing fails, keep the original filters
      console.warn('Failed to parse date for sort_date optimization:', error);
    }
  }

  const {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery(
    ['tableOverview', processedFilters],
    async ({ pageParam: cursor }) => {
      const requestFilters = { ...processedFilters };
      if (cursor) {
        requestFilters.cursor = cursor;
      }
      const res =
        await api.client.tableOverview.getTableOverview(requestFilters);
      return res.result;
    },
    {
      ...defaultOptions,
      ...options,
      getNextPageParam: (lastPage) => {
        // If we have a cursor and the number of users is less than the limit,
        // we've reached the end
        if (!lastPage?.cursor) {
          return null;
        }

        // If we got fewer users than the limit, we've reached the end
        if (lastPage.users.length < (processedFilters.limit ?? 30)) {
          return null;
        }

        return lastPage.cursor;
      }
    }
  );

  // Flatten all users from all pages for virtual list compatibility
  const allUsers = data?.pages.flatMap((page) => page?.users ?? []) ?? [];
  // Get the latest page data for other information
  const latestPage = data?.pages[data.pages.length - 1];
  // const allUsers = latestPage?.users ?? [];

  // Combine all days data from all pages (since days data is cumulative)
  const allDays =
    data?.pages.reduce(
      (acc, page) => {
        if (page?.days) {
          Object.assign(acc, page.days);
        }
        return acc;
      },
      {} as Record<
        string,
        { people_in_office: number; favorite_people_in_office: number }
      >
    ) ?? {};

  return {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    // Flattened data for virtual list compatibility
    allUsers,
    allDays,
    // Latest page data for totals
    totalPeopleInOffice: latestPage?.total_people_in_office ?? 0,
    totalFavoritePeopleInOffice:
      latestPage?.total_favorite_people_in_office ?? 0
  };
}
